import type { AxiosError } from 'axios';
import { authClient } from './client';
import type {
  ConsignorInventoryItem,
  ConsignorInventoryPayload,
  InventoryItemStatus,
  ItemCondition,
} from '../types/consignorInventory';

// ─── Parser ───────────────────────────────────────────────────────────────────

function parseItem(raw: unknown): ConsignorInventoryItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id) || id <= 0) return null;

  const artIdRaw = r.artId ?? r.art_id;
  const imageUrlRaw = r.imageUrl ?? r.image_url;
  const deactivatedAtRaw = r.deactivatedAt ?? r.deactivated_at;
  const receivedAtRaw = r.receivedAt ?? r.received_at;

  const conditionRaw = String(
    r.condition ?? r.conditionLabel ?? r.condition_label ?? ''
  ).toLowerCase();
  const condition: ItemCondition =
    conditionRaw.includes('used') || conditionRaw === 'usado' ? 'used' : 'new';

  const statusRaw = String(r.status ?? '').toLowerCase();
  const status: InventoryItemStatus = statusRaw === 'deactivated' ? 'deactivated' : 'active';

  return {
    id,
    artId: typeof artIdRaw === 'string' && artIdRaw.trim() ? artIdRaw.trim() : null,
    productName: String(r.productName ?? r.product_name ?? r.title ?? '').trim(),
    imageUrl: typeof imageUrlRaw === 'string' && imageUrlRaw.trim() ? imageUrlRaw.trim() : null,
    sizeName: String(r.sizeName ?? r.size_name ?? r.size ?? '').trim(),
    condition,
    status,
    receivedAt: typeof receivedAtRaw === 'string' && receivedAtRaw ? receivedAtRaw : null,
    deactivatedAt:
      typeof deactivatedAtRaw === 'string' && deactivatedAtRaw ? deactivatedAtRaw : null,
  };
}

// ─── Exports ──────────────────────────────────────────────────────────────────

export type ConsignorInventoryResult =
  | { ok: true; data: ConsignorInventoryPayload }
  | { ok: false; error: string };

/**
 * GET /api/consignor/inventory
 *
 * Returns the current consignor's active and recently deactivated inventory.
 * Sold and returned items are excluded (those appear in the sales page).
 * Ordered by receivedAt DESC (most recent first).
 *
 * Query params (all optional):
 *   search   – substring match on product name
 *
 * Response 200 JSON:
 * {
 *   "message": "OK",
 *   "data": {
 *     "items": [
 *       {
 *         "id": 1,                     // inventory row id
 *         "artId": "42",               // nullable
 *         "productName": "...",
 *         "imageUrl": "https://...",   // nullable
 *         "sizeName": "9.5 US",
 *         "condition": "new" | "used",
 *         "status": "active" | "deactivated",
 *         "receivedAt": "2024-01-01T00:00:00Z",   // nullable
 *         "deactivatedAt": null                    // ISO, set when deactivated
 *       }
 *     ],
 *     "total": 12
 *   }
 * }
 *
 * Errors: 403 not authenticated, 500 server error.
 */
export async function fetchConsignorInventory(search?: string): Promise<ConsignorInventoryResult> {
  try {
    const params: Record<string, string> = {};
    if (search?.trim()) params.search = search.trim();
    const { data } = await authClient.get<unknown>('/api/consignor/inventory', { params });
    const body = data as Record<string, unknown>;
    const inner = (typeof body.data === 'object' && body.data ? body.data : body) as Record<
      string,
      unknown
    >;
    const rawItems = inner.items;
    const items = Array.isArray(rawItems)
      ? rawItems.map(parseItem).filter((x): x is ConsignorInventoryItem => x !== null)
      : [];
    const total = typeof inner.total === 'number' ? inner.total : items.length;
    return { ok: true, data: { items, total } };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo cargar tu inventario.',
    };
  }
}

export type DeactivateItemResult = { ok: true } | { ok: false; error: string };

/**
 * PATCH /api/consignor/inventory/:id/deactivate
 *
 * Marks the consignor's own inventory item as deactivated. The consignor has
 * 5 calendar days to pick it up. After that the backend automatically sets the
 * status back to active.
 *
 * Auth: consignor must own this inventory record.
 *
 * Request body: {} (empty)
 *
 * Response 200: { "message": "OK" }
 * Errors:
 *   403 – item does not belong to this consignor.
 *   404 – item not found.
 *   409 – item is already deactivated or not in an active/unsold state.
 *   500 – server error.
 */
export async function deactivateConsignorItem(inventoryId: number): Promise<DeactivateItemResult> {
  try {
    await authClient.patch(`/api/consignor/inventory/${inventoryId}/deactivate`, {});
    return { ok: true };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo desactivar el artículo.',
    };
  }
}
